'use client';

import { useQuery } from '@apollo/client/react';
import { IntercomUserTokenDocument } from '@graphql/queries/intercom-user-token.graphql';
import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk';
import type { WorkspaceType } from '@modules/user-profile/user-profile-details-validator';
import { useEffect } from 'react';

// Public Intercom workspace app id + region (FIN chatbot). Not a secret; the
// app id is exposed client-side by design, matching how the previous support
// widget embedded its public key.
const INTERCOM_APP_ID = 'uf06g5fl';
const INTERCOM_REGION = 'ap';

type IntercomMessengerProps = {
  /** Authenticated user id used to identify the visitor in Intercom. */
  userId: string;
  /** Display name, when available. */
  name?: string;
  /** Email address, when available. */
  email?: string;
  /** Current workspace type, sent as a custom attribute for display rules. */
  workspaceType: WorkspaceType;
};

// Intercom launcher's default gap from the bottom of the viewport.
const DEFAULT_VERTICAL_PADDING = 20;

/**
 * Raises the Intercom launcher by `padding` px while the calling component is
 * mounted, restoring the default gap on unmount. Use to keep the launcher clear
 * of sticky bottom bars (e.g. the campaign form footer).
 */
export const useIntercomVerticalPadding = (padding: number) => {
  useEffect(() => {
    update({ vertical_padding: padding });
    return () => update({ vertical_padding: DEFAULT_VERTICAL_PADDING });
  }, [padding]);
};

/**
 * Boots the Intercom (FIN) messenger for an authenticated dashboard user.
 *
 * Mounted only in the dashboard shell layouts, so the widget never appears in
 * onboarding flows. Replaces the previous Zendesk support widget.
 */
export const IntercomMessenger = ({
  userId,
  name,
  email,
  workspaceType,
}: IntercomMessengerProps) => {
  const { data } = useQuery(IntercomUserTokenDocument);

  useEffect(() => {
    if (!data?.intercomUserToken) return;

    Intercom({
      app_id: INTERCOM_APP_ID,
      region: INTERCOM_REGION,
      user_id: userId,
      name,
      email,
      intercom_user_jwt: data.intercomUserToken,
      // Custom attribute used to drive display/audience rules in Intercom.
      workspace_type: workspaceType,
    });

    // Tear down when leaving the dashboard shell (e.g. client-side navigation
    // to an onboarding route) so the widget does not linger on excluded pages.
    return () => shutdown();
  }, [userId, name, email, workspaceType, data?.intercomUserToken]);

  return null;
};
